import { useCallback } from "react"

import { useMount, useUnmount } from "react-use"

enum EWindowCustomEvent {
    AssistantMeta = "event:assistantMeta",
    CoachGroupMeta = "event:coachGroupMeta",
    CoachGroupParticipantMeta = "event:coachGroupParticipantMeta",
    CoachIndividualParticipantMeta = "event:coachIndividualParticipantMeta"
}

type TWindowCustomEventPayloadMap = {
    [EWindowCustomEvent.AssistantMeta]: { firstName: string; lastName: string; photo: string | null }
    [EWindowCustomEvent.CoachGroupMeta]: { groupName: string; cohortName: string }
    [EWindowCustomEvent.CoachGroupParticipantMeta]: {
        photo: string
        firstName: string
        lastName: string
        cohortName: string
        groupName: string
    }
    [EWindowCustomEvent.CoachIndividualParticipantMeta]: {
        photo: string
        firstName: string
        lastName: string
        cohortName: string
    }
}
type TWindowCustomEvent<EName extends EWindowCustomEvent> = CustomEvent<TWindowCustomEventPayloadMap[EName]>
type TWindowCustomEventHandler<EName extends EWindowCustomEvent = EWindowCustomEvent> = (
    e: TWindowCustomEvent<EName>
) => void
type TWindowCustomEventDispatcher<EName extends EWindowCustomEvent = EWindowCustomEvent> = (
    payload: TWindowCustomEventPayloadMap[EName]
) => void

function useWindowCustomEventListener<EEventName extends EWindowCustomEvent>(
    name: EEventName,
    handler: TWindowCustomEventHandler<EEventName>
): void {
    useMount((): void => window.addEventListener(name, handler))

    useUnmount((): void => window.removeEventListener(name, handler))
}

function useWindowCustomEventDispatcher<EEventName extends EWindowCustomEvent>(
    eventName: EEventName
): ReturnType<(name: EEventName) => TWindowCustomEventDispatcher> {
    return useCallback(
        (payload: TWindowCustomEventPayloadMap[EEventName]): void =>
            void window.dispatchEvent(new CustomEvent(eventName, { detail: payload })),
        [eventName]
    )
}

export { useWindowCustomEventListener, useWindowCustomEventDispatcher }
export { EWindowCustomEvent, type TWindowCustomEventDispatcher, type TWindowCustomEventPayloadMap }
